/** Drive : navigation dossiers/fichiers, vues spéciales, actions, partage. */
import { el, clear, escapeHtml } from '../ui/dom.js';
import { icon, TYPE_META, fileKind } from '../ui/icons.js';
import { Drive } from '../api.js';
import { toast } from '../ui/toast.js';
import { contextMenu, promptModal, confirmModal, openModal } from '../ui/modal.js';

const EDIT_ROUTE = { doc: '/doc/', sheet: '/sheet/', slides: '/slides/' };

export function openRoute(node) {
  if (node.type === 'folder') return '/drive/' + node.id;
  return (EDIT_ROUTE[node.type] || '/doc/') + node.id;
}

function nodeMeta(node) {
  return node.type === 'file' ? fileKind(node) : (TYPE_META[node.type] || TYPE_META.doc);
}

/** Ouvre un node : dossier -> navigation ; fichier importé -> raw ; éditeur -> nouvel onglet. */
export function openNode(node, navigate) {
  if (node.type === 'folder') navigate(openRoute(node));
  else if (node.type === 'file') window.open('/api/drive/file/' + node.id + '/raw', '_blank', 'noopener');
  else window.open(openRoute(node), '_blank', 'noopener');
}

/** Importe des fichiers dans un dossier. */
export async function uploadFiles(files, parent) {
  let done = 0;
  for (const f of files) {
    const fd = new FormData(); fd.append('file', f); if (parent) fd.append('parent', parent);
    try {
      const res = await fetch('/api/drive/upload', { method: 'POST', body: fd, credentials: 'same-origin' });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'échec'); }
      done++;
    } catch (e) { toast(`« ${f.name} » : ${e.message}`, 'err'); }
  }
  if (done) toast(done > 1 ? `${done} fichiers importés` : 'Fichier importé');
  return done;
}

function timeAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  const d = Math.floor(s / 86400);
  if (d < 30) return `il y a ${d} j`;
  return new Date(ts).toLocaleDateString('fr-FR');
}

/** Carte fichier/dossier réutilisable. */
export function fileTile(node, { navigate, onMore }) {
  const meta = nodeMeta(node);
  const tile = el('div', { class: 'tile', tabindex: '0' },
    el('div', { class: 'ic', style: { background: meta.color }, html: icon(meta.icon, 22) }),
    el('div', { class: 'nm' }, node.name),
    el('div', { class: 'meta' }, (node.type === 'folder' ? 'Dossier' : meta.label) + ' · ' + timeAgo(node.updated_at)),
    el('span', { class: 'star' + (node.starred ? ' on' : ''), html: icon('star', 16) })
  );
  const open = () => openNode(node, navigate);
  tile.addEventListener('click', open);
  tile.addEventListener('keydown', (e) => { if (e.key === 'Enter') open(); });
  if (onMore) {
    const more = el('button', { class: 'more', html: icon('more', 18), title: 'Options' });
    more.addEventListener('click', (e) => { e.stopPropagation(); onMore(node, e); });
    tile.appendChild(more);
    tile.addEventListener('contextmenu', (e) => { e.preventDefault(); onMore(node, e); });
  }
  return tile;
}

const VIEW_TITLES = {
  folder: 'Mon Drive', recent: 'Récents', starred: 'Favoris', shared: 'Partagés avec moi', trash: 'Corbeille',
};

export function renderDrive(ctx, mode, folderId) {
  const { content, navigate, shell, user } = ctx;
  shell.currentFolder = mode === 'folder' ? folderId : null;

  let viewKind = localStorage.getItem('jhn-drive-view') || 'grid';
  let sortKey = localStorage.getItem('jhn-drive-sort') || 'name';
  let sortDir = localStorage.getItem('jhn-drive-dir') || 'asc';
  let allNodes = [];
  let term = '';

  const SORT_LABELS = { name: 'Nom', date: 'Date de création', size: 'Taille' };
  function sortNodes(arr) {
    const dir = sortDir === 'desc' ? -1 : 1;
    return arr.slice().sort((a, b) => {
      if ((a.type === 'folder') !== (b.type === 'folder')) return a.type === 'folder' ? -1 : 1; // dossiers d'abord
      let r = 0;
      if (sortKey === 'name') r = a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });
      else if (sortKey === 'date') r = (a.created_at || 0) - (b.created_at || 0);
      else if (sortKey === 'size') r = (a.size || 0) - (b.size || 0);
      return r * dir;
    });
  }
  function setSort(k) { sortKey = k; localStorage.setItem('jhn-drive-sort', k); buildToolbar(); paint(); }
  function toggleDir() { sortDir = sortDir === 'asc' ? 'desc' : 'asc'; localStorage.setItem('jhn-drive-dir', sortDir); buildToolbar(); paint(); }

  const toolbar = el('div', { class: 'toolbar' });
  const itemsWrap = el('div', {});
  content.append(toolbar, itemsWrap);

  // Import de fichiers (bouton + glisser-déposer) — uniquement dans un dossier à soi.
  const canUpload = mode === 'folder';
  const fileInput = el('input', { type: 'file', multiple: true, accept: '.mp4,.mp3,.pdf,.jpg,.jpeg,.png,.md,.webp,.gif,.m4a,.mov,.txt,image/*,video/*,audio/*,application/pdf', style: { display: 'none' } });
  fileInput.addEventListener('change', async () => { if (fileInput.files.length) { await uploadFiles(fileInput.files, folderId); load(); } fileInput.value = ''; });
  content.appendChild(fileInput);

  let dragDepth = 0;
  const onDragOver = (e) => { if (canUpload) e.preventDefault(); };
  const onDragEnter = (e) => { if (!canUpload) return; e.preventDefault(); dragDepth++; content.classList.add('drag-over'); };
  const onDragLeave = () => { if (--dragDepth <= 0) { dragDepth = 0; content.classList.remove('drag-over'); } };
  const onDrop = async (e) => { if (!canUpload) return; e.preventDefault(); dragDepth = 0; content.classList.remove('drag-over'); const files = e.dataTransfer && e.dataTransfer.files; if (files && files.length) { await uploadFiles(files, folderId); load(); } };
  if (canUpload) { content.addEventListener('dragover', onDragOver); content.addEventListener('dragenter', onDragEnter); content.addEventListener('dragleave', onDragLeave); content.addEventListener('drop', onDrop); }

  function buildToolbar() {
    clear(toolbar);
    toolbar.append(el('h2', {}, VIEW_TITLES[mode] || 'Drive'));
    if (mode === 'trash') {
      const empty = el('button', { class: 'btn btn-sm', html: icon('trash', 16) + 'Vider la corbeille' });
      empty.addEventListener('click', emptyTrash);
      toolbar.appendChild(empty);
    }
    if (canUpload) {
      const imp = el('button', { class: 'btn btn-sm', html: icon('upload', 16) + 'Importer' });
      imp.addEventListener('click', () => fileInput.click());
      toolbar.appendChild(imp);
    }
    // Tri : nom / date de création / taille, croissant ou décroissant.
    const sortBtn = el('button', { class: 'btn btn-sm', html: icon('list', 16) + 'Trier : ' + SORT_LABELS[sortKey] + ' ' + (sortDir === 'asc' ? '↑' : '↓') });
    sortBtn.addEventListener('click', (e) => {
      const r = e.currentTarget.getBoundingClientRect();
      contextMenu(r.left, r.bottom + 4, [
        { label: 'Nom', icon: sortKey === 'name' ? 'check' : 'rename', onClick: () => setSort('name') },
        { label: 'Date de création', icon: sortKey === 'date' ? 'check' : 'recent', onClick: () => setSort('date') },
        { label: 'Taille', icon: sortKey === 'size' ? 'check' : 'file', onClick: () => setSort('size') },
        { sep: true },
        { label: sortDir === 'asc' ? 'Ordre décroissant ↓' : 'Ordre croissant ↑', icon: 'move', onClick: toggleDir },
      ]);
    });
    toolbar.appendChild(sortBtn);
    const seg = el('div', { class: 'seg' }, segBtn('grid', 'grid'), segBtn('list', 'list'));
    toolbar.appendChild(seg);
  }
  function segBtn(kind, ic) {
    const b = el('button', { class: viewKind === kind ? 'active' : '', html: icon(ic, 17), title: kind });
    b.addEventListener('click', () => { viewKind = kind; localStorage.setItem('jhn-drive-view', kind); buildToolbar(); paint(); });
    return b;
  }

  async function load() {
    try {
      const params = mode === 'folder' ? (folderId ? { parent: folderId } : {}) : { view: mode };
      const { nodes } = await Drive.list(params);
      allNodes = nodes;
      await setCrumbs();
      paint();
    } catch (e) {
      itemsWrap.innerHTML = '';
      itemsWrap.appendChild(emptyState('x', 'Erreur', e.message));
    }
  }

  async function setCrumbs() {
    if (mode !== 'folder') { ctx.setCrumbs([{ label: VIEW_TITLES[mode] }]); return; }
    const parts = [{ label: 'Mon Drive', path: '/drive' }];
    if (folderId) {
      try {
        const { breadcrumb } = await Drive.breadcrumb(folderId);
        breadcrumb.forEach((b, i) => parts.push({ label: b.name, path: '/drive/' + b.id }));
      } catch {}
    }
    ctx.setCrumbs(parts);
  }

  function filtered() {
    const t = term.trim().toLowerCase();
    return t ? allNodes.filter((n) => n.name.toLowerCase().includes(t)) : allNodes;
  }

  function paint() {
    clear(itemsWrap);
    const nodes = sortNodes(filtered());
    if (!nodes.length) {
      itemsWrap.appendChild(emptyForMode());
      return;
    }
    if (viewKind === 'list') itemsWrap.appendChild(listView(nodes));
    else {
      const grid = el('div', { class: 'grid' });
      nodes.forEach((n) => grid.appendChild(fileTile(n, { navigate, onMore: showMenu })));
      itemsWrap.appendChild(grid);
    }
  }

  function listView(nodes) {
    const list = el('div', { class: 'list' });
    list.appendChild(el('div', { class: 'head' }, el('span', { class: 'nm' }, 'Nom'), el('span', { class: 'meta' }, 'Modifié')));
    nodes.forEach((n) => {
      const meta = nodeMeta(n);
      const row = el('div', { class: 'row' },
        el('div', { class: 'ic', style: { background: meta.color }, html: icon(meta.icon, 17) }),
        el('div', { class: 'nm' }, n.name + (n.starred ? '  ★' : '')),
        el('div', { class: 'meta' }, timeAgo(n.updated_at))
      );
      row.addEventListener('click', () => openNode(n, navigate));
      row.addEventListener('contextmenu', (e) => { e.preventDefault(); showMenu(n, e); });
      list.appendChild(row);
    });
    return list;
  }

  function emptyForMode() {
    if (term) return emptyState('search', 'Aucun résultat', `Rien ne correspond à « ${escapeHtml(term)} ».`);
    const map = {
      folder: ['folder', 'Dossier vide', 'Glissez des fichiers ou cliquez sur « Nouveau » pour commencer.'],
      recent: ['recent', 'Aucun fichier récent', 'Vos fichiers récemment modifiés apparaîtront ici.'],
      starred: ['star', 'Aucun favori', 'Marquez un fichier d’une étoile pour le retrouver ici.'],
      shared: ['shared', 'Rien de partagé', 'Les fichiers que l’on partage avec vous apparaîtront ici.'],
      trash: ['trash', 'Corbeille vide', 'Les éléments supprimés atterrissent ici.'],
    };
    const [ic, title, sub] = map[mode] || map.folder;
    return emptyState(ic, title, sub);
  }

  // ── Menu contextuel d'un élément ──
  function showMenu(node, e) {
    const x = e.clientX, y = e.clientY;
    if (mode === 'trash') {
      contextMenu(x, y, [
        { label: 'Restaurer', icon: 'restore', onClick: () => act(() => Drive.restore(node.id), 'Restauré') },
        { sep: true },
        { label: 'Supprimer définitivement', icon: 'trash', danger: true, onClick: async () => {
          if (await confirmModal({ title: 'Supprimer définitivement ?', message: `« ${node.name} » sera supprimé pour toujours.`, confirm: 'Supprimer', danger: true }))
            act(() => Drive.remove(node.id), 'Supprimé');
        } },
      ]);
      return;
    }
    contextMenu(x, y, [
      { label: 'Ouvrir', icon: node.type === 'folder' ? 'folder' : 'doc', onClick: () => navigate(openRoute(node)) },
      { label: 'Renommer', icon: 'rename', onClick: () => rename(node) },
      { label: node.starred ? 'Retirer des favoris' : 'Ajouter aux favoris', icon: 'star', onClick: () => act(() => Drive.update(node.id, { starred: !node.starred }), node.starred ? 'Retiré des favoris' : 'Ajouté aux favoris') },
      { label: 'Déplacer vers…', icon: 'move', onClick: () => move(node) },
      { label: node.type === 'folder' ? 'Partager le dossier' : 'Partager', icon: 'share', onClick: () => shareModal(node) },
      { sep: true },
      { label: 'Mettre à la corbeille', icon: 'trash', danger: true, onClick: () => act(() => Drive.trash(node.id), 'Déplacé vers la corbeille') },
    ]);
  }

  async function rename(node) {
    const name = await promptModal({ title: 'Renommer', label: 'Nom', value: node.name, confirm: 'Renommer' });
    if (name && name !== node.name) act(() => Drive.update(node.id, { name }), 'Renommé');
  }
  async function move(node) {
    const dest = await pickFolder(node.id);
    if (dest === undefined) return;
    act(() => Drive.update(node.id, { parent: dest }), 'Déplacé');
  }
  async function act(fn, msg) {
    try { await fn(); toast(msg); load(); } catch (e) { toast(e.message, 'err'); }
  }
  async function emptyTrash() {
    if (!allNodes.length) return;
    if (!(await confirmModal({ title: 'Vider la corbeille ?', message: 'Tous les éléments seront supprimés définitivement.', confirm: 'Vider', danger: true }))) return;
    try { await Promise.all(allNodes.map((n) => Drive.remove(n.id))); toast('Corbeille vidée'); load(); }
    catch (e) { toast(e.message, 'err'); }
  }

  // refresh externe (création depuis la sidebar)
  const onRefresh = () => load();
  window.addEventListener('jhn:refresh', onRefresh);

  ctx.setSearch((v) => { term = v; paint(); });

  buildToolbar();
  load();

  return () => {
    window.removeEventListener('jhn:refresh', onRefresh);
    if (canUpload) { content.removeEventListener('dragover', onDragOver); content.removeEventListener('dragenter', onDragEnter); content.removeEventListener('dragleave', onDragLeave); content.removeEventListener('drop', onDrop); content.classList.remove('drag-over'); }
  };
}

function emptyState(ic, title, sub) {
  return el('div', { class: 'empty' },
    el('div', { class: 'ico', html: icon(ic, 30) }),
    el('h3', {}, title),
    el('div', {}, sub));
}

/** Sélecteur de dossier (mini-navigateur). Retourne folderId | null (racine) | undefined (annulé). */
function pickFolder(excludeId) {
  return new Promise((resolve) => {
    let current = null;
    let chain = [{ id: null, name: 'Mon Drive' }];
    openModal((modal, close) => {
      const crumbEl = el('div', { class: 'crumbs', style: { marginBottom: '10px', fontSize: '14px' } });
      const listEl = el('div', { style: { maxHeight: '300px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' } });
      async function show(folder) {
        const { nodes } = await Drive.list(folder ? { parent: folder } : {});
        clear(listEl);
        const folders = nodes.filter((n) => n.type === 'folder' && n.id !== excludeId);
        if (!folders.length) listEl.appendChild(el('div', { style: { color: 'var(--text-faint)', padding: '12px', fontSize: '14px' } }, 'Aucun sous-dossier.'));
        folders.forEach((f) => {
          const row = el('div', { class: 'menu-item', html: icon('folder', 17) + escapeHtml(f.name) });
          row.addEventListener('click', () => { chain.push({ id: f.id, name: f.name }); current = f.id; paintCrumbs(); show(f.id); });
          listEl.appendChild(row);
        });
      }
      function paintCrumbs() {
        clear(crumbEl);
        chain.forEach((c, i) => {
          if (i) crumbEl.appendChild(el('span', { class: 'sep', html: icon('chevron', 14) }));
          const s = el('span', { class: 'crumb', style: { cursor: 'pointer' } }, c.name);
          s.addEventListener('click', () => { chain = chain.slice(0, i + 1); current = c.id; paintCrumbs(); show(c.id); });
          crumbEl.appendChild(s);
        });
      }
      paintCrumbs();
      show(null);
      modal.append(
        el('h3', {}, 'Déplacer vers'),
        crumbEl, listEl,
        el('div', { class: 'row' },
          el('button', { class: 'btn btn-ghost', onclick: () => { close(); resolve(undefined); } }, 'Annuler'),
          el('button', { class: 'btn btn-primary', onclick: () => { close(); resolve(current); } }, 'Déplacer ici'))
      );
    });
  });
}

/** Modale de partage (collaboration). */
export function shareModal(node) {
  openModal((modal, close) => {
    const list = el('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px', margin: '6px 0 16px' } });
    const input = el('input', { class: 'input', placeholder: 'Email ou Rayor ID du collaborateur' });
    async function refresh() {
      clear(list);
      try {
        const { shares } = await Drive.shares(node.id);
        if (!shares.length) { list.appendChild(el('div', { style: { color: 'var(--text-faint)', fontSize: '14px' } }, 'Aucun collaborateur pour le moment.')); return; }
        shares.forEach((s) => {
          const row = el('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
            el('div', { class: 'avatar sm' }, ((s.given_name || s.email || '?')[0] || '?').toUpperCase()),
            el('div', { style: { flex: 1 } }, el('div', { style: { fontSize: '14px', fontWeight: 560 } }, `${s.given_name || ''} ${s.family_name || ''}`.trim() || s.nickname || s.email),
              el('div', { style: { fontSize: '12px', color: 'var(--text-faint)' } }, s.email || '')),
            roleSelect(s.role, async (r) => { try { await Drive.share(node.id, s.email || s.nickname, r); toast('Rôle : ' + (r === 'viewer' ? 'Lecteur' : 'Éditeur')); } catch (e) { toast(e.message, 'err'); refresh(); } }));
          const rm = el('button', { class: 'btn btn-sm btn-icon btn-ghost', html: icon('x', 15), title: 'Retirer' });
          rm.addEventListener('click', async () => { try { await Drive.unshare(node.id, s.id); refresh(); } catch (e) { toast(e.message, 'err'); } });
          row.appendChild(rm);
          list.appendChild(row);
        });
      } catch (e) { list.appendChild(el('div', { style: { color: '#ff7a7a' } }, e.message)); }
    }
    const newRole = roleSelect('editor');
    const add = async () => {
      const id = input.value.trim();
      if (!id) return;
      try { await Drive.share(node.id, id, newRole.value); input.value = ''; toast('Partagé'); refresh(); }
      catch (e) { toast(e.message, 'err'); }
    };
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') add(); });
    refresh();
    modal.append(
      el('h3', {}, 'Partager « ' + node.name + ' »'),
      el('p', { class: 'sub' }, 'Travaillez à plusieurs en temps réel. La personne doit déjà s’être connectée une fois à JHN Suite.'),
      list,
      el('div', { style: { display: 'flex', gap: '8px' } }, input, newRole, el('button', { class: 'btn btn-primary', onclick: add, html: icon('plus', 16) })),
      el('div', { class: 'row' }, el('button', { class: 'btn btn-ghost', onclick: close }, 'Terminé'))
    );
  });
}

/** Petit sélecteur de rôle (Éditeur/Lecteur). onChange optionnel. */
function roleSelect(value, onChange) {
  const sel = el('select', { class: 'role-select' },
    el('option', { value: 'editor' }, 'Éditeur'),
    el('option', { value: 'viewer' }, 'Lecteur'));
  sel.value = value === 'viewer' ? 'viewer' : 'editor';
  if (onChange) sel.addEventListener('change', () => onChange(sel.value));
  return sel;
}
